/**
 * Theme Management System
 *
 * Pure JavaScript theme management without framework dependencies
 */

import { THEMES, THEME_STORAGE_KEY, THEME_DATA_ATTRIBUTE, THEME_CLASS_PREFIX } from '../constants'
import type { ThemeName, Theme } from '../types'

/**
 * Get theme object by name
 */
export function getTheme(name: ThemeName): Theme {
  return THEMES[name]
}

/**
 * Get all available theme names
 */
export function getThemeNames(): ThemeName[] {
  return Object.keys(THEMES) as ThemeName[]
}

/**
 * Apply theme to document
 */
export function applyTheme(themeName: ThemeName): void {
  const htmlElement = document.documentElement
  htmlElement.setAttribute(THEME_DATA_ATTRIBUTE, themeName)
  htmlElement.className = `${THEME_CLASS_PREFIX}${themeName}`

  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeName)
  } catch (error) {
    console.warn('[BizFirst] Failed to save theme preference:', error)
  }

  // Dispatch custom event for theme change
  window.dispatchEvent(
    new CustomEvent('bizfirst:theme-change', {
      detail: { theme: themeName },
    })
  )
}

/**
 * Get current applied theme from DOM
 */
export function getCurrentTheme(): ThemeName {
  const theme = document.documentElement.getAttribute(THEME_DATA_ATTRIBUTE)
  return (theme as ThemeName) || 'dark'
}

/**
 * Get saved theme preference from localStorage
 */
export function getSavedTheme(): ThemeName | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) as ThemeName | null
  } catch (error) {
    console.warn('[BizFirst] Failed to read saved theme:', error)
    return null
  }
}

/**
 * Initialize theme system
 */
export function initialize(defaultTheme: ThemeName = 'dark'): ThemeName {
  const saved = getSavedTheme()
  const theme = saved || defaultTheme
  applyTheme(theme)
  return theme
}

/**
 * Toggle between dark and light themes
 */
export function toggleTheme(): ThemeName {
  const current = getCurrentTheme()
  const next = current === 'dark' ? 'light' : 'dark'
  applyTheme(next)
  return next
}

/**
 * Listen for theme changes
 */
export function onThemeChange(callback: (theme: ThemeName) => void): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent
    callback(customEvent.detail.theme)
  }

  window.addEventListener('bizfirst:theme-change', handler)

  // Return unsubscribe function
  return () => {
    window.removeEventListener('bizfirst:theme-change', handler)
  }
}

/**
 * Get CSS variable value for current theme
 */
export function getCSSVariable(variable: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim()
  return value
}

/**
 * Get all colors for current theme
 */
export function getCurrentThemeColors() {
  const current = getCurrentTheme()
  return getTheme(current).colors
}

/**
 * Check if dark theme is active
 */
export function isDarkTheme(): boolean {
  return getCurrentTheme() === 'dark'
}

/**
 * Check if light theme is active
 */
export function isLightTheme(): boolean {
  return getCurrentTheme() === 'light'
}

/**
 * Theme utilities object
 */
export const ThemeUtils = {
  getTheme,
  getThemeNames,
  applyTheme,
  getCurrentTheme,
  getSavedTheme,
  initialize,
  toggleTheme,
  onThemeChange,
  getCSSVariable,
  getCurrentThemeColors,
  isDarkTheme,
  isLightTheme,
}

export default ThemeUtils
